import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import TestAutomation from "@/components/TestAutomation";
import { ACCENT_PRESETS, applyAccentColor, isValidHexColor } from "@/lib/theme";
import {
  MessageSquareText,
  Building2,
  Users,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  Star,
  Save,
  Sliders,
  ChevronRight,
  Palette,
  Check,
  RefreshCw,
  Send,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const inputCls =
  "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-500";
const primaryBtn =
  "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn =
  "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";

function SectionCard({ title, icon: Icon, action, children }) {
  return (
    <div className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30">
      <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-3">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-orange-600" />
          </div>
          <h3 className="font-heading text-lg font-bold text-white">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

const QUALITY = {
  GREEN: { dot: "bg-green-500", label: "High quality", cls: "text-green-400 bg-green-500/10" },
  YELLOW: { dot: "bg-yellow-400", label: "Medium quality", cls: "text-yellow-300 bg-yellow-500/10" },
  RED: { dot: "bg-red-500", label: "Low quality", cls: "text-red-400 bg-red-500/10" },
};

function MetaBadges({ s, loading }) {
  const chip = "inline-flex items-center gap-1.5 text-[11px] font-semibold rounded px-2 py-0.5";
  if (!s) {
    return loading ? <p className="text-xs text-slate-500 mt-1.5">Checking Meta…</p> : null;
  }
  if (s.error) {
    return <p className="text-xs text-red-400 mt-1.5 break-words">Meta error: {s.error}</p>;
  }
  const q = QUALITY[(s.quality || "").toUpperCase()];
  const status = (s.status || "").toUpperCase();
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
      {s.limit_label && (
        <span className={`${chip} text-orange-400 bg-orange-500/10`} title={s.limit_scope === "shared" ? "Shared by all numbers in the business portfolio" : "Limit for this number"}>
          Limit {s.limit_label}/day{s.limit_scope === "shared" ? " (shared)" : ""}
        </span>
      )}
      {q && <span className={`${chip} ${q.cls}`}><span className={`h-2 w-2 rounded-full ${q.dot}`} />{q.label}</span>}
      {status && (
        <span className={`${chip} ${status === "CONNECTED" ? "text-green-400 bg-green-500/10" : "text-red-400 bg-red-500/10"}`}>
          {status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ")}
        </span>
      )}
      {s.throughput && <span className={`${chip} text-slate-300 bg-white/10`}>Throughput: {s.throughput.toLowerCase()}</span>}
      {s.display_phone_number && <span className="text-[11px] text-slate-500">{s.display_phone_number}</span>}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { branches, activeBranch, activeBranchId, loadBranches } = useBranch();
  const isOwner = user?.role === "owner";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // WhatsApp connection form
  const [wa, setWa] = useState({ phone_number_id: "", waba_id: "", access_token: "", display_phone_number: "" });
  const [savingWa, setSavingWa] = useState(false);

  // Live Meta limits/quality per branch (backend caches 10 min)
  const [metaStatus, setMetaStatus] = useState({});
  const [metaLoading, setMetaLoading] = useState(false);
  const loadMetaStatus = async (refresh = false) => {
    setMetaLoading(true);
    try {
      const { data } = await api.get("/branches/meta-status", { params: refresh ? { refresh: true } : {} });
      setMetaStatus(data?.branches || {});
    } catch (e) { /* ignore */ }
    finally { setMetaLoading(false); }
  };
  useEffect(() => {
    if (isOwner) loadMetaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  // Appearance / accent color
  const [accentColor, setAccentColor] = useState(null);
  const [savingAccent, setSavingAccent] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    api
      .get("/settings/appearance")
      .then(({ data }) => setAccentColor(data?.accent_color || null))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const saveAccentColor = async (hex) => {
    if (!isValidHexColor(hex)) return;
    setSavingAccent(true);
    applyAccentColor(hex); // instant preview while it saves
    try {
      await api.put("/settings/appearance", { accent_color: hex });
      setAccentColor(hex);
      toast.success("Accent color updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingAccent(false);
    }
  };

  useEffect(() => {
    if (activeBranch) {
      setWa({
        phone_number_id: activeBranch.phone_number_id || "",
        waba_id: activeBranch.waba_id || "",
        access_token: activeBranch.access_token || "",
        display_phone_number: activeBranch.display_phone_number || "",
      });
    }
  }, [activeBranch]);

  useEffect(() => {
    if (searchParams.get("section") === "branches") {
      setBranchDialog({ open: true, editing: null, name: "", is_default: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const saveWa = async () => {
    if (!activeBranchId) return;
    setSavingWa(true);
    try {
      await api.put(`/branches/${activeBranchId}`, wa);
      await loadBranches();
      toast.success("WhatsApp connection saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingWa(false);
    }
  };

  // Branch dialog
  const [branchDialog, setBranchDialog] = useState({
    open: false,
    editing: null,
    name: "",
    is_default: false,
  });
  const [savingBranch, setSavingBranch] = useState(false);
  const [deleteBranch, setDeleteBranch] = useState(null);

  const submitBranch = async () => {
    setSavingBranch(true);
    try {
      if (branchDialog.editing) {
        await api.put(`/branches/${branchDialog.editing}`, {
          name: branchDialog.name,
          is_default: branchDialog.is_default,
        });
        toast.success("Branch updated");
      } else {
        await api.post("/branches", {
          name: branchDialog.name,
          is_default: branchDialog.is_default,
        });
        toast.success("Branch added");
      }
      await loadBranches();
      setBranchDialog({ open: false, editing: null, name: "", is_default: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingBranch(false);
    }
  };

  const confirmDeleteBranch = async () => {
    try {
      await api.delete(`/branches/${deleteBranch.id}`);
      await loadBranches();
      toast.success("Branch deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleteBranch(null);
    }
  };

  // Staff
  const [staff, setStaff] = useState([]);
  const [staffDialog, setStaffDialog] = useState({
    open: false,
    name: "",
    email: "",
    password: "",
    branch_id: "",
  });
  const [savingStaff, setSavingStaff] = useState(false);
  const [deleteStaff, setDeleteStaff] = useState(null);

  const loadStaff = async () => {
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (isOwner) loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const submitStaff = async () => {
    setSavingStaff(true);
    try {
      await api.post("/staff", staffDialog);
      toast.success("Staff login created");
      await loadStaff();
      setStaffDialog({ open: false, name: "", email: "", password: "", branch_id: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingStaff(false);
    }
  };

  const confirmDeleteStaff = async () => {
    try {
      await api.delete(`/staff/${deleteStaff.id}`);
      await loadStaff();
      toast.success("Staff removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleteStaff(null);
    }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl tracking-tight font-black text-white">
          Settings
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Configure WhatsApp, branches and staff logins.
        </p>
      </div>

      <div className="flex flex-col gap-8 max-w-4xl">
        {/* WhatsApp Connection */}
        <SectionCard title="WhatsApp Connection" icon={MessageSquareText}>
          <p className="text-sm text-slate-500 mb-5">
            Credentials for{" "}
            <span className="font-semibold text-white">
              {activeBranch ? activeBranch.name : "—"}
            </span>{" "}
            branch.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Phone Number ID</label>
              <input
                data-testid="wa-phone-number-id-input"
                className={`mt-1.5 ${inputCls}`}
                value={wa.phone_number_id}
                onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })}
                placeholder="e.g. 123456789012345"
              />
            </div>
            <div>
              <label className={labelCls}>Display Phone Number</label>
              <input
                data-testid="wa-display-phone-number-input"
                className={`mt-1.5 ${inputCls}`}
                value={wa.display_phone_number}
                onChange={(e) => setWa({ ...wa, display_phone_number: e.target.value })}
                placeholder="e.g. +91 86573 10651"
              />
              <p className="text-xs text-slate-500 mt-1">Shown next to the branch name in the sidebar — the number customers actually message.</p>
            </div>
            <div>
              <label className={labelCls}>WABA ID</label>
              <input
                data-testid="wa-waba-id-input"
                className={`mt-1.5 ${inputCls}`}
                value={wa.waba_id}
                onChange={(e) => setWa({ ...wa, waba_id: e.target.value })}
                placeholder="e.g. 987654321098765"
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Access Token</label>
              <input
                data-testid="wa-access-token-input"
                className={`mt-1.5 ${inputCls}`}
                value={wa.access_token}
                onChange={(e) => setWa({ ...wa, access_token: e.target.value })}
                placeholder="Permanent access token"
              />
            </div>
          </div>
          <div className="mt-6">
            <button
              data-testid="wa-save-button"
              className={primaryBtn}
              onClick={saveWa}
              disabled={savingWa || !activeBranchId}
            >
              {savingWa ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Connection
            </button>
          </div>
        </SectionCard>

        {/* Test automation */}
        {isOwner && (
          <SectionCard title="Test automation" icon={Send}>
            <p className="text-sm text-slate-500 mb-5">
              Send a sample WhatsApp message to verify a template is working.
            </p>
            <TestAutomation />
          </SectionCard>
        )}

        {/* Appearance */}
        {isOwner && (
          <SectionCard title="Appearance" icon={Palette}>
            <p className="text-sm text-slate-500 mb-5">
              Pick an accent color — it applies everywhere across the app instantly, for everyone.
            </p>

            <div className="flex flex-wrap gap-3 mb-5">
              {ACCENT_PRESETS.map((preset) => {
                const isActive = (accentColor || "").toLowerCase() === preset.hex.toLowerCase();
                return (
                  <button
                    key={preset.key}
                    data-testid={`accent-preset-${preset.key}`}
                    type="button"
                    title={preset.name}
                    onClick={() => saveAccentColor(preset.hex)}
                    className={`relative h-11 w-11 rounded-full transition-all duration-150 active:scale-90 ${
                      isActive ? "ring-2 ring-offset-2 ring-offset-[#141414] ring-white" : "hover:scale-110"
                    }`}
                    style={{ background: preset.hex }}
                  >
                    {isActive && (
                      <Check className="h-4 w-4 text-white absolute inset-0 m-auto drop-shadow" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <label className={labelCls}>Custom color</label>
              <input
                data-testid="accent-custom-color-input"
                type="color"
                value={accentColor || "#EA580C"}
                onChange={(e) => saveAccentColor(e.target.value)}
                className="h-10 w-16 rounded-md border border-white/20 bg-transparent cursor-pointer disabled:opacity-60"
                disabled={savingAccent}
              />
              {savingAccent && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
            </div>
          </SectionCard>
        )}

        {/* Automation Settings */}
        {isOwner && (
          <SectionCard title="Automation" icon={Sliders}>
            <button
              data-testid="open-automation-settings-button"
              onClick={() => navigate("/automation-settings")}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-white/10 hover:bg-white/5 transition-all duration-150 text-left"
            >
              <div>
                <p className="text-sm font-semibold text-white">Automation Settings</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Turn triggers on/off and pick which template each one sends
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
            </button>
          </SectionCard>
        )}

        {/* Branches */}
        {isOwner && (
          <SectionCard
            title="Branches"
            icon={Building2}
            action={
              <div className="flex items-center gap-2">
                <button
                  data-testid="refresh-meta-status"
                  title="Refresh Meta limits"
                  className={secondaryBtn}
                  onClick={() => loadMetaStatus(true)}
                  disabled={metaLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${metaLoading ? "animate-spin" : ""}`} />
                  Meta limits
                </button>
                <button
                  data-testid="add-branch-button"
                  className={primaryBtn}
                  onClick={() =>
                    setBranchDialog({ open: true, editing: null, name: "", is_default: false })
                  }
                >
                  <Plus className="h-4 w-4" />
                  Add Branch
                </button>
              </div>
            }
          >
            <div className="divide-y divide-white/10 border border-white/10 rounded-md overflow-hidden">
              {branches.map((b) => (
                <div
                  key={b.id}
                  data-testid={`branch-row-${b.id}`}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white text-sm">{b.name}</span>
                    {b.is_default && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold text-orange-600 bg-orange-500/10 rounded px-2 py-0.5">
                        <Star className="h-3 w-3" /> Default
                      </span>
                    )}
                    {b.phone_number_id && (
                      <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-green-400 bg-green-500/10 rounded px-2 py-0.5">
                        Connected
                      </span>
                    )}
                  </div>
                  {b.phone_number_id && <MetaBadges s={metaStatus[b.id]} loading={metaLoading} />}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`edit-branch-${b.id}`}
                      className="p-2 rounded-md text-slate-500 hover:text-white hover:bg-white/10 transition-all duration-150"
                      onClick={() =>
                        setBranchDialog({
                          open: true,
                          editing: b.id,
                          name: b.name,
                          is_default: b.is_default,
                        })
                      }
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`delete-branch-${b.id}`}
                      className="p-2 rounded-md text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
                      onClick={() => setDeleteBranch(b)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Staff */}
        {isOwner && (
          <SectionCard
            title="Staff"
            icon={Users}
            action={
              <button
                data-testid="add-staff-button"
                className={primaryBtn}
                onClick={() =>
                  setStaffDialog({
                    open: true,
                    name: "",
                    email: "",
                    password: "",
                    branch_id: activeBranchId || "",
                  })
                }
              >
                <Plus className="h-4 w-4" />
                Add Staff
              </button>
            }
          >
            {staff.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">
                No staff logins yet. Create one to give branch-locked access.
              </p>
            ) : (
              <div className="divide-y divide-white/10 border border-white/10 rounded-md overflow-hidden">
                {staff.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`staff-row-${s.id}`}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div>
                      <p className="font-semibold text-white text-sm">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-slate-300 bg-white/10 rounded px-2 py-1">
                        {branchName(s.branch_id)}
                      </span>
                      <button
                        data-testid={`delete-staff-${s.id}`}
                        className="p-2 rounded-md text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
                        onClick={() => setDeleteStaff(s)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        )}
      </div>

      {/* Branch dialog */}
      <Dialog
        open={branchDialog.open}
        onOpenChange={(o) => setBranchDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent data-testid="branch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {branchDialog.editing ? "Edit Branch" : "Add Branch"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Branch Name</label>
              <input
                data-testid="branch-name-input"
                className={`mt-1.5 ${inputCls}`}
                value={branchDialog.name}
                onChange={(e) => setBranchDialog({ ...branchDialog, name: e.target.value })}
                placeholder="e.g. Powai"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input
                data-testid="branch-default-checkbox"
                type="checkbox"
                className="h-4 w-4 accent-orange-600"
                checked={branchDialog.is_default}
                onChange={(e) =>
                  setBranchDialog({ ...branchDialog, is_default: e.target.checked })
                }
              />
              Set as default branch
            </label>
          </div>
          <DialogFooter>
            <button
              className={secondaryBtn}
              onClick={() => setBranchDialog({ ...branchDialog, open: false })}
            >
              Cancel
            </button>
            <button
              data-testid="branch-save-button"
              className={primaryBtn}
              onClick={submitBranch}
              disabled={savingBranch || !branchDialog.name.trim()}
            >
              {savingBranch && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff dialog */}
      <Dialog
        open={staffDialog.open}
        onOpenChange={(o) => setStaffDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Staff Login</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Name</label>
              <input
                data-testid="staff-name-input"
                className={`mt-1.5 ${inputCls}`}
                value={staffDialog.name}
                onChange={(e) => setStaffDialog({ ...staffDialog, name: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                data-testid="staff-email-input"
                type="email"
                className={`mt-1.5 ${inputCls}`}
                value={staffDialog.email}
                onChange={(e) => setStaffDialog({ ...staffDialog, email: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <input
                data-testid="staff-password-input"
                type="text"
                className={`mt-1.5 ${inputCls}`}
                value={staffDialog.password}
                onChange={(e) => setStaffDialog({ ...staffDialog, password: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Assigned Branch</label>
              <Select
                value={staffDialog.branch_id}
                onValueChange={(v) => setStaffDialog({ ...staffDialog, branch_id: v })}
              >
                <SelectTrigger data-testid="staff-branch-select" className="mt-1.5">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`staff-branch-opt-${b.id}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button
              className={secondaryBtn}
              onClick={() => setStaffDialog({ ...staffDialog, open: false })}
            >
              Cancel
            </button>
            <button
              data-testid="staff-save-button"
              className={primaryBtn}
              onClick={submitStaff}
              disabled={
                savingStaff ||
                !staffDialog.name.trim() ||
                !staffDialog.email.trim() ||
                !staffDialog.password ||
                !staffDialog.branch_id
              }
            >
              {savingStaff && <Loader2 className="h-4 w-4 animate-spin" />}
              Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete branch confirm */}
      <AlertDialog open={!!deleteBranch} onOpenChange={(o) => !o && setDeleteBranch(null)}>
        <AlertDialogContent data-testid="delete-branch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteBranch?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Branches with contacts, messages or staff cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-branch"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteBranch}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete staff confirm */}
      <AlertDialog open={!!deleteStaff} onOpenChange={(o) => !o && setDeleteStaff(null)}>
        <AlertDialogContent data-testid="delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteStaff?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This staff member will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-staff"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteStaff}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
